import { getApiHosts, getDefaultHeaders, spaHeaderOptions, type Env } from '@fff-web/constants';
import { createRequest } from '@fff-web/request';
import type { FetchOptions } from '@fff-web/request';
import type {
  CreateScratcherBody,
  CreateScratcherParams,
  CreateScratcherPrizeBody,
  CreateScratcherPrizeParams,
  GetScratcherPrize,
  GetScratcherPrizesParams,
  RedemptionCouponCodeData,
  RedemptionCouponCodeParams,
  RedemptionErrorData,
  ScratcherEvent,
  ScratcherPrize,
  UpdateScratcherParams,
  UpdateScratcherPrizeParams,
  WithCookieAuth,
} from '@fff-web/sdk-types';
import type {
  BulkUploadCouponCodes,
  CountryLocationApiResponse,
  GetUserSubscriptionsResponse,
  RetrieveMarketingCookieParams,
} from './types';

/**
 * Growth SDK.
 * @description
 * Growth SDK is a simple function that takes the environment as parameter (`env`) and
 * returns a set of functions to interact with the growth API. The url used
 * will depend on the environment passed when initializing the SDK.
 *
 * The functions from this SDK are meant to be used on any environment, so
 * the redirect and credentials properties are set to the default fetch values.
 * This is because API calls in Maui will always hit the NextJS API routes,
 * which will handle the redirection. This also means that credentials will
 * be sent only on same-origin requests.
 *
 * If you want to use this in the client, you can initialize
 * the SDK with the parameter `spa = true` to set the redirect policy to
 * manual and credentials to be always included. Please remember that this
 * is not recommended as it has potential security implications. In the future
 * this option will be removed.
 *
 * For more information about the Growth API please check the following links:
 * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/
 */
export function growthSdk(env: Env, spa = false, host?: string) {
  if (!env) {
    throw new Error('Environment is required.');
  }

  const options = spa ? spaHeaderOptions : {};
  const defaultHeaders = getDefaultHeaders(env);
  if (!host) {
    const { GROWTH_API_BASE_HOST } = getApiHosts(env, ['GROWTH_API_BASE_HOST']);
    host = `${GROWTH_API_BASE_HOST}`;
  }

  return {
    /**
     * Get country ISO code based on IP address.
     * @returns {Promise<CountryLocationApiResponse>} The country ISO code response.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/default/getCountryByIp
     */
    getCountryIsoCode: (userIp: string, fetchOptions?: FetchOptions) =>
      createRequest<void, CountryLocationApiResponse>({
        url: `${host}/location/country-by-ip?ipAddress=${userIp}`,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
          ...(fetchOptions || {}),
        },
      })(),
    /**
     * Redeem a promo code or gift card code.
     * @param {RedemptionCouponCodeParams} params - The parameters for redeeming a coupon code.
     * @returns {Promise<RedemptionCouponCodeData>} The response from the redemption API.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/default/getRedemption
     */
    redemptionCouponCode: (params: RedemptionCouponCodeParams) => {
      const { promoCodeOrGiftCardCode, planCode, countryCode, postalCode, state, city } = params;
      const queryParams = new URLSearchParams();

      if (planCode) queryParams.append('planCode', planCode);
      if (countryCode) queryParams.append('countryCode', countryCode);
      if (postalCode) queryParams.append('postalCode', postalCode);
      if (state) queryParams.append('state', state);
      if (city) queryParams.append('city', city);

      const queryString = queryParams.toString();
      const url = `${host}/coupons/redemption-code/${promoCodeOrGiftCardCode}${queryString ? `?${queryString}` : ''}`;

      return createRequest<
        RedemptionCouponCodeParams,
        RedemptionCouponCodeData,
        RedemptionErrorData
      >({
        url,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })();
    },
    /**
     * Sets server-side cookie for advertising merchants.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/default/setCookie
     */
    retrieveMarketingCookie: (params: RetrieveMarketingCookieParams) =>
      createRequest<RetrieveMarketingCookieParams, void>({
        url: `${host}/marketing/cookie`,
        method: 'POST',
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(params),
    /**
     * Gets user subscriptions.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/default/getUserSubscriptions
     */
    getUserSubscriptions: async (jwtToken?: string) =>
      createRequest<void, GetUserSubscriptionsResponse>({
        url: `${host}/v1/subscriptions/self`,
        fetchOptions: {
          headers: {
            ...defaultHeaders,
            ...(jwtToken ? { Cookie: `Jwt=${jwtToken}` } : {}),
          },
          ...options,
        },
      })(),
    /**
     * Cancels user subscription.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/cancel
     */
    cancelUserSubscription: async (subscriptionId: string) =>
      createRequest<void, void>({
        url: `${host}/v1/subscriptions/self/subscription-uuid/${subscriptionId}/cancel`,
        method: 'PATCH',
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(),
    /**
     * Reactivates a cancelled subscription.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/Subscription%20Service/reactivate
     */
    reactivateUserSubscription: async (subscriptionId: string) =>
      createRequest<void, void>({
        url: `${host}/v1/subscriptions/self/subscription-uuid/${subscriptionId}/reactivate`,
        method: 'PATCH',
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(),
    /**
     * Retrieve all scratcher events.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/public-virtual-scratchers-controller/getAllScratcherEvents
     */
    getAllScratcherEvents: async () =>
      createRequest<void, ScratcherEvent[]>({
        url: `${host}/public/v1/scratchers/events`,
        fetchOptions: {
          headers: defaultHeaders,
          ...options,
        },
      })(),
    /**
     * Retrieve all user scratcher events.
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/public-virtual-scratchers-controller/getAllScratcherEvents
     */
    getAllUserScratcherEvents: async ({ jwtCookie }: WithCookieAuth) =>
      createRequest<void, ScratcherEvent[]>({
        url: `${host}/v1/scratchers/events/self`,
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(),
    /**
     * Create new scratcher event
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/createScratcherEvent
     */
    createScratcherEvent: async ({ jwtCookie, body }: CreateScratcherParams) =>
      createRequest<CreateScratcherBody, ScratcherEvent>({
        url: `${host}/v1/scratchers/events`,
        method: 'POST',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(body),
    /**
     * Update scratcher event
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/updateScratcherEvent
     */
    updateScratcherEvent: async ({ jwtCookie, body, id }: UpdateScratcherParams) =>
      createRequest<Partial<CreateScratcherBody>, ScratcherEvent>({
        url: `${host}/v1/scratchers/events/${id}`,
        method: 'PUT',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(body),
    /**
     * Retrieve sctracher event prizes
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/getScratcherPrizesByEventId
     */
    getScratcherPrizes: async ({ jwtCookie, id }: GetScratcherPrizesParams) =>
      createRequest<CreateScratcherPrizeBody, GetScratcherPrize[]>({
        url: `${host}/v1/scratchers/events/${id}/prizes`,
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(),
    /**
     * Create new scratcher prize
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/createScratcherPrize
     */
    createScratcherPrize: async ({ jwtCookie, body }: CreateScratcherPrizeParams) =>
      createRequest<CreateScratcherPrizeBody, ScratcherPrize>({
        url: `${host}/v1/scratchers/prizes`,
        method: 'POST',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(body),
    /**
     * Update scratcher prize
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/updateScratcherPrize
     */
    updateScratcherPrize: async ({ jwtCookie, prizeId, body }: UpdateScratcherPrizeParams) =>
      createRequest<Partial<CreateScratcherPrizeBody>, ScratcherPrize>({
        url: `${host}/v1/scratchers/prizes/${prizeId}`,
        method: 'PUT',
        fetchOptions: {
          headers: { ...defaultHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(body),
    /**
     * Upload coupon codes to bind them to a prize
     * @see {link} https://swagger.fff-dev.com/?url=%2Fspecs%3Fenv%3Ddev%26service%3Dgrowth-api#/virtual-scratchers-controller/uploadCouponCodes
     */
    uploadCouponCodes: async ({
      jwtCookie,
      eventId,
      prizeId,
      expirationDate,
      file,
    }: BulkUploadCouponCodes) => {
      const queryParams = new URLSearchParams({ eventId, prizeId, expirationDate });

      const formData = new FormData();
      formData.append('file', new Blob([file], { type: 'text/csv' }), 'coupons.csv');

      // Omit Content-Type so fetch sets the multipart boundary automatically
      const { 'Content-Type': _ct, ...multipartHeaders } = defaultHeaders as Record<string, string>;

      return createRequest<FormData, void>({
        url: `${host}/v1/scratchers/coupons/bulk?${queryParams.toString()}`,
        method: 'POST',
        fetchOptions: {
          headers: { ...multipartHeaders, Cookie: jwtCookie },
          ...options,
        },
      })(formData);
    },
  };
}
